/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Travel mode for a Walk Me Home session: "walk" (default, original behavior)
// or "drive" (car / rideshare — an emergency contact tracks you home while you
// drive or take an Uber). This module is the SINGLE source of truth for the
// mode-dependent constants — pace and the "too far" distance cap — so ETA math,
// the picker's cap check, and the running-ETA recompute all agree. Copy/labels
// are i18n'd in the components; this stays pure + framework-free so it's
// unit-testable and shared between client and (future) server.

export type TravelMode = "walk" | "drive";

export const DEFAULT_TRAVEL_MODE: TravelMode = "walk";

export const TRAVEL_MODES: readonly TravelMode[] = ["walk", "drive"];

export function isTravelMode(v: unknown): v is TravelMode {
  return v === "walk" || v === "drive";
}

/** Normalize an unknown/legacy value to a mode. Sessions written before drive
 *  mode existed have no travelMode field → treated as "walk" (their true kind). */
export function normalizeTravelMode(v: unknown): TravelMode {
  return isTravelMode(v) ? v : DEFAULT_TRAVEL_MODE;
}

// Effective average pace in metres/second, used for the straight-line ETA
// estimate. These are deliberately CONSERVATIVE effective speeds (door-to-door,
// including stops/turns), not free-flow speeds — an ETA that runs a little long
// is safer than one that trips the overdue alert early.
//  - walk:  1.4 m/s ≈ 5.0 km/h ≈ 3.1 mph (unchanged from the original constant)
//  - drive: 8.33 m/s ≈ 30 km/h ≈ 18.6 mph (effective urban door-to-door; a real
//    road-route ETA replaces this straight-line estimate in a later change)
const PACE_MPS: Record<TravelMode, number> = {
  walk: 1.4,
  drive: 8.33,
};

// Maximum straight-line distance (km) the picker will accept for each mode. A
// walk beyond ~20 km isn't a realistic "walk me home"; driving legitimately
// covers far more, so the cap is much higher (but still bounded so a wildly
// wrong pin is caught).
const MAX_DISTANCE_KM: Record<TravelMode, number> = {
  walk: 20,
  drive: 500,
};

export function paceMpsFor(mode: TravelMode): number {
  return PACE_MPS[normalizeTravelMode(mode)];
}

export function maxDistanceKmFor(mode: TravelMode): number {
  return MAX_DISTANCE_KM[normalizeTravelMode(mode)];
}

/**
 * Straight-line ETA in whole minutes for a given distance + mode. Mirrors the
 * original walking formula (distanceKm / 5 km/h) generalized over the mode's
 * pace. Always ≥ 1. Distance in kilometres.
 */
export function etaMinutesForDistanceKm(
  distanceKm: number,
  mode: TravelMode,
): number {
  const paceMps = paceMpsFor(mode);
  if (paceMps <= 0 || !isFinite(distanceKm) || distanceKm < 0) return 1;
  const meters = distanceKm * 1000;
  return Math.max(1, Math.ceil(meters / paceMps / 60));
}
